import { useMemo } from 'react'
import { create } from 'zustand'
import { get, post, openMarketSocket, type Ticker } from '../lib/api'

/* ------------------------------------------------------------------ احراز هویت */
export type User = {
  id: number; email: string; mobile: string; fullName: string
  mobileVerified?: boolean; smsNotify?: boolean; smsTwoFa?: boolean
  kycLevel: number; kycStatus: string; twoFa: boolean; isAdmin: boolean
  referralCode: string; referralEarnings: number; createdAt: string; avatarSeed: string
  totalIrt?: number
}

export type LoginPayload = {
  email?: string; mobile?: string; password?: string; otp?: string; sms_code?: string
}

export type RegisterPayload = {
  email?: string; password?: string; mobile?: string
  code?: string; ticket?: string; referral?: string
  /** استعلام هویت — وقتی سایت آن را الزامی کرده باشد */
  first_name?: string; last_name?: string; national_id?: string
  birth_date?: string
}

type AuthState = {
  user: User | null
  loading: boolean
  init: () => Promise<void>
  login: (payload: LoginPayload) => Promise<void>
  register: (payload: RegisterPayload) => Promise<void>
  /** ورود مستقیم با توکنی که از تأیید کد پیامکی گرفته شده */
  adopt: (token: string, user: User) => void
  logout: () => void
  refresh: () => Promise<void>
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  loading: true,
  init: async () => {
    if (!localStorage.getItem('az_token')) return set({ loading: false })
    try {
      set({ user: await get<User>('/auth/me'), loading: false })
    } catch {
      localStorage.removeItem('az_token')
      set({ user: null, loading: false })
    }
  },
  login: async (payload) => {
    const r = await post<{ token: string; user: User }>('/auth/login', payload)
    localStorage.setItem('az_token', r.token)
    set({ user: r.user })
  },
  register: async (payload) => {
    const r = await post<{ token: string; user: User }>('/auth/register', payload)
    localStorage.setItem('az_token', r.token)
    set({ user: r.user })
  },
  adopt: (token, user) => {
    localStorage.setItem('az_token', token)
    set({ user })
  },
  logout: () => {
    localStorage.removeItem('az_token')
    set({ user: null })
  },
  refresh: async () => {
    try {
      set({ user: await get<User>('/auth/me') })
    } catch {
      /* noop */
    }
  },
}))

// وقتی سرور نشست را نپذیرفت، حالت «وارد شده» هم باید برود؛ وگرنه Guard اجازه
// می‌دهد صفحه‌های شخصی باز شوند ولی همه درخواست‌هایشان خالی برمی‌گردد.
if (typeof window !== 'undefined') {
  window.addEventListener('arzino:unauthorized', () => {
    if (useAuth.getState().user) useAuth.setState({ user: null, loading: false })
  })
}

/* ------------------------------------------------------------------ بازار */
type MarketState = {
  tickers: Record<string, Ticker>
  source: string
  usdtIrt: number
  connected: boolean
  symbolData: any
  start: () => void
  stop: () => void
  subscribe: (symbol: string) => void
}

let socket: ReturnType<typeof openMarketSocket> | null = null

export const useMarket = create<MarketState>((set, getState) => ({
  tickers: {},
  source: '',
  usdtIrt: 0,
  connected: false,
  symbolData: null,
  start: () => {
    if (socket) return
    get('/market/tickers')
      .then((r: any) => {
        const map: Record<string, Ticker> = {}
        r.data.forEach((t: Ticker) => (map[t.symbol] = t))
        set({ tickers: map, source: r.source, usdtIrt: r.usdtIrt })
      })
      .catch(() => {})
    socket = openMarketSocket((msg) => {
      if (msg.type === 'tickers') {
        const map = { ...getState().tickers }
        msg.data.forEach((t: Ticker) => (map[t.symbol] = t))
        set({
          tickers: map, source: msg.source, usdtIrt: msg.usdtIrt,
          connected: true, symbolData: msg.symbolData ?? getState().symbolData,
        })
      }
    })
  },
  stop: () => {
    socket?.close()
    socket = null
    set({ connected: false })
  },
  subscribe: (symbol) => socket?.subscribe(symbol),
}))

function sortTickers(tickers: Record<string, Ticker>, quote?: string): Ticker[] {
  const all = Object.values(tickers)
  const rows = quote ? all.filter((t) => t.quote === quote) : all
  return [...rows].sort((a, b) => a.rank - b.rank)
}

/**
 * فهرست واکنشی ارزها.
 *
 * همیشه از این هوک استفاده کنید، نه از یک سلکتورِ تابعی.
 *
 * سابقه: قبلاً فروشگاه متدی به نام `list()` داشت و صفحه‌ها آن را با
 * `useMarket((s) => s.list)` می‌گرفتند. ارجاعِ یک تابع هرگز عوض نمی‌شود، پس
 * zustand — که با Object.is مقایسه می‌کند — با رسیدن قیمت‌های تازه هیچ رندری
 * انجام نمی‌داد و صفحه خالی می‌ماند تا کاربر رفرش کند. آن متد کلاً حذف شد تا
 * دوباره به اشتباه استفاده نشود؛ این هوک مستقیماً به خودِ داده مشترک می‌شود.
 */
export function useTickers(quote?: string): Ticker[] {
  const tickers = useMarket((s) => s.tickers)
  return useMemo(() => sortTickers(tickers, quote), [tickers, quote])
}

/** آیا داده بازار آماده است (برای تمایز «در حال بارگذاری» از «خالی») */
export function useMarketReady(): boolean {
  return useMarket((s) => Object.keys(s.tickers).length > 0)
}

/* ------------------------------------------------------------------ اعلان‌ها */
export type Toast = { id: number; kind: 'success' | 'error' | 'info'; text: string }

type ToastState = {
  items: Toast[]
  push: (kind: Toast['kind'], text: string) => void
  remove: (id: number) => void
}

export const useToast = create<ToastState>((set) => ({
  items: [],
  push: (kind, text) => {
    const id = Date.now() + Math.random()
    set((s) => ({ items: [...s.items, { id, kind, text }] }))
    setTimeout(() => set((s) => ({ items: s.items.filter((t) => t.id !== id) })), 4200)
  },
  remove: (id) => set((s) => ({ items: s.items.filter((t) => t.id !== id) })),
}))

export const toast = {
  ok: (t: string) => useToast.getState().push('success', t),
  err: (t: string) => useToast.getState().push('error', t),
  info: (t: string) => useToast.getState().push('info', t),
}
